import { useCallback, useEffect, useState, type ReactNode } from "react";
import { AnimatePresence, motion } from "motion/react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Loader2 } from "@/components/ui/icons";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { extractApiErrorMessage } from "@/lib/api";
import {
  cancelPendingOrder,
  listPendingOrders,
  type PendingRow,
} from "@/lib/api/manual-trade";

/**
 * Live pending-order table. Fetches once on mount + refreshes on every
 * `pending:changed` ServerEvent the page ingests. We expose `refresh`
 * via a ref so the parent can call it when WS events fire; we don't
 * take the ServerEvent directly here to keep the component unaware of
 * the global store shape.
 */
export interface PendingOrdersTableHandle {
  refresh(): Promise<void>;
}

export function PendingOrdersTable({
  pairSlug,
  onRefRef,
}: {
  pairSlug: string;
  /**
   * Setter the parent invokes with the refresh callback. Lets the
   * parent trigger a re-fetch on `pending:changed` events without
   * using a React ref forwarding dance.
   */
  onRefRef?: (fn: () => Promise<void>) => void;
}): ReactNode {
  const [rows, setRows] = useState<PendingRow[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [cancellingId, setCancellingId] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listPendingOrders(pairSlug);
      setRows(res.pending);
    } catch (e) {
      toast.error(extractApiErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }, [pairSlug]);

  useEffect(() => {
    void refresh();
    onRefRef?.(refresh);
  }, [refresh, onRefRef]);

  const cancel = async (id: string) => {
    setCancellingId(id);
    try {
      await cancelPendingOrder(id);
      toast.success("Pending canceled");
      setRows((prev) => prev?.filter((r) => r.id !== id) ?? null);
    } catch (e) {
      toast.error(extractApiErrorMessage(e));
    } finally {
      setCancellingId(null);
    }
  };

  return (
    <div className="flex flex-col overflow-hidden rounded-xl border border-border bg-card/60">
      <div className="flex items-center justify-between gap-2 border-b border-border/60 px-3 py-2">
        <span className="text-xs font-semibold uppercase tracking-wider text-foreground">
          Pending Orders
        </span>
        <span className="font-mono text-[10px] text-muted-foreground">
          {rows?.length ?? 0} active
        </span>
      </div>

      {rows === null && loading ? (
        <div aria-busy="true" aria-label="Loading pending orders" className="flex flex-col gap-2 px-3 py-3">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="flex items-center justify-between gap-3 rounded-md border border-border/30 bg-muted/20 px-3 py-2"
            >
              <Skeleton className="h-4 w-16" />
              <Skeleton className="h-4 w-10" />
              <Skeleton className="h-4 w-12" />
              <Skeleton className="h-4 w-16" />
              <Skeleton className="h-6 w-14" />
            </div>
          ))}
        </div>
      ) : rows && rows.length === 0 ? (
        <div className="py-6 text-center text-xs text-muted-foreground">
          No pending orders for this pair.
        </div>
      ) : (
        <div className="hidden overflow-x-auto md:block">
          <table className="w-full text-xs">
            <thead className="bg-muted/30 text-[10px] uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="px-3 py-2 text-left">Symbol</th>
                <th className="px-3 py-2 text-left">Side</th>
                <th className="px-3 py-2 text-right">Lots</th>
                <th className="px-3 py-2 text-right">Trigger</th>
                <th className="px-3 py-2 text-right">SL</th>
                <th className="px-3 py-2 text-right">TP</th>
                <th className="px-3 py-2 text-right">Expires</th>
                <th className="px-3 py-2 text-right" />
              </tr>
            </thead>
            <tbody>
              <AnimatePresence initial={false}>
                {(rows ?? []).map((r) => (
                  <motion.tr
                    key={r.id}
                    layout
                    initial={{ opacity: 0, y: -4 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -4 }}
                    className="border-t border-border/40"
                  >
                    <td className="px-3 py-2 font-mono">{r.symbol}</td>
                    <td className="px-3 py-2">
                      <Badge
                        variant="outline"
                        className={cn(
                          "text-[10px] uppercase",
                          r.side === "buy"
                            ? "border-emerald-500/30 text-emerald-500"
                            : "border-rose-500/30 text-rose-500",
                        )}
                      >
                        {r.side}
                      </Badge>
                    </td>
                    <td className="px-3 py-2 text-right font-mono tabular-nums">
                      {r.lots}
                    </td>
                    <td className="px-3 py-2 text-right font-mono tabular-nums">
                      {r.triggerDirection === "ge" ? "bid ≥" : "ask ≤"}{" "}
                      {r.triggerPrice}
                    </td>
                    <td className="px-3 py-2 text-right font-mono tabular-nums text-amber-500">
                      {r.sl}
                    </td>
                    <td className="px-3 py-2 text-right font-mono tabular-nums text-emerald-500/80">
                      {r.tp ?? "—"}
                    </td>
                    <td className="px-3 py-2 text-right font-mono text-[10px] text-muted-foreground">
                      {new Date(r.expiresAt).toLocaleTimeString()}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => cancel(r.id)}
                        disabled={cancellingId === r.id}
                        aria-busy={cancellingId === r.id}
                        className="h-6 border-destructive/40 px-2 text-[10px] text-destructive hover:bg-destructive/10"
                      >
                        {cancellingId === r.id && (
                          <Loader2 aria-hidden="true" className="mr-1 h-3 w-3 animate-spin" />
                        )}
                        {cancellingId === r.id ? "Cancelling…" : "Cancel"}
                      </Button>
                    </td>
                  </motion.tr>
                ))}
              </AnimatePresence>
            </tbody>
          </table>
        </div>
      )}

      {rows && rows.length > 0 && (
        <ul className="flex flex-col gap-2 p-3 md:hidden">
          <AnimatePresence initial={false}>
            {rows.map((r) => (
              <motion.li
                key={`mobile:${r.id}`}
                layout
                initial={{ opacity: 0, y: -4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -4 }}
                className="flex flex-col gap-2 rounded-lg border border-border/40 bg-muted/20 p-3 text-xs"
              >
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <Badge
                      variant="outline"
                      className={cn(
                        "text-[10px] uppercase",
                        r.side === "buy"
                          ? "border-emerald-500/30 text-emerald-500"
                          : "border-rose-500/30 text-rose-500",
                      )}
                    >
                      {r.side}
                    </Badge>
                    <span className="font-mono text-sm font-medium text-foreground">
                      {r.symbol}
                    </span>
                  </div>
                  <span className="font-mono text-[10px] text-muted-foreground">
                    {new Date(r.expiresAt).toLocaleTimeString()}
                  </span>
                </div>
                <div className="font-mono text-[11px] text-foreground">
                  {r.triggerDirection === "ge" ? "bid ≥" : "ask ≤"} {r.triggerPrice}
                </div>
                <div className="grid grid-cols-3 gap-2 text-[11px]">
                  <div>
                    <span className="block text-[10px] uppercase tracking-wider text-muted-foreground">Lots</span>
                    <span className="font-mono tabular-nums">{r.lots}</span>
                  </div>
                  <div>
                    <span className="block text-[10px] uppercase tracking-wider text-muted-foreground">SL</span>
                    <span className="font-mono tabular-nums text-amber-500">{r.sl}</span>
                  </div>
                  <div>
                    <span className="block text-[10px] uppercase tracking-wider text-muted-foreground">TP</span>
                    <span className="font-mono tabular-nums text-emerald-500/80">{r.tp ?? "—"}</span>
                  </div>
                </div>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => cancel(r.id)}
                  disabled={cancellingId === r.id}
                  aria-busy={cancellingId === r.id}
                  className="h-7 border-destructive/40 text-[11px] text-destructive hover:bg-destructive/10"
                >
                  {cancellingId === r.id && (
                    <Loader2 aria-hidden="true" className="mr-1 h-3 w-3 animate-spin" />
                  )}
                  {cancellingId === r.id ? "Cancelling…" : "Cancel"}
                </Button>
              </motion.li>
            ))}
          </AnimatePresence>
        </ul>
      )}
    </div>
  );
}
